import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  User,
  MoreHorizontal,
  AlertTriangle,
  TrendingUp,
  Check,
  ArrowRight,
  MapPin,
  Mail,
  MessageCircle,
  Bell,
  ArrowDown,
  BarChart3,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { DataTable } from "@/components/ui/data-table";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useActiveProgramCurrency } from "../programs/program-scope";

interface Member360CustomerAnalyticsProps {
  mobileNo: string;
}

export function Member360CustomerAnalytics({
  mobileNo,
}: Member360CustomerAnalyticsProps) {
  const { currency } = useActiveProgramCurrency();
  // Sample data - replace with actual API data
  const memberData = {
    name: "Rahul Sharma",
    tier: "Gold Tier",
    address1: "Connaugt Itp:S J.VO",
    address2: "Delhi (Conaught Place)",
  };

  const campaignHistory = [
    {
      id: "1",
      date: "Jan 12",
      offer: `${currency} 150 off`,
      channel: "WhatsApp",
      revenue: `${currency} 1,200`,
      redeemed: "Yes",
    },
    {
      id: "2",
      date: "Feb 03",
      offer: "Scratch",
      channel: "POS",
      revenue: "...",
      redeemed: "No",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {/* Top Section */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="h-8 w-8 text-gray-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {memberData.name}
                  </h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm text-gray-600">
                      {memberData.tier}
                    </span>
                    <span className="text-sm text-gray-600">{mobileNo}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Section */}
            <div className="border-t pt-4 pb-4 bg-gray-50 -mx-6 px-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Gold Tier Member
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-gray-500" />
                  <BarChart3 className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">333</span>
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    {memberData.address1}
                  </span>
                </div>
                <Check className="h-4 w-4 text-green-500" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    {memberData.address2}
                  </span>
                </div>
                <Check className="h-4 w-4 text-green-500" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Best Offer and Predicted Spend Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Next Best Offer Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Next Best Offer
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="text-2xl font-semibold text-gray-900">{currency}</span>
              <span className="text-sm text-red-600 font-medium ml-1">
                High Risk of Leaving
              </span>
            </div>
            <p className="text-sm text-gray-600">in 30 Days</p>
            <div className="space-y-1">
              <Progress value={20} className="h-2" />
              <div className="flex gap-1">
                <div className="h-2 w-8 bg-gray-800 rounded"></div>
                <div className="h-2 w-8 bg-gray-300 rounded"></div>
                <div className="h-2 w-8 bg-gray-300 rounded"></div>
                <div className="h-2 w-8 bg-gray-300 rounded"></div>
                <div className="h-2 w-8 bg-gray-300 rounded"></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Predicted Spend Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Check className="h-4 w-4" />
                Predicted Spend
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-2xl font-semibold text-gray-900">
                  {currency} 6,200
                </span>
                <p className="text-sm text-gray-600 mt-1">next 3 days</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="h-8 w-2 bg-gray-300 rounded"
                      style={{ height: `${20 + i * 5}px` }}
                    ></div>
                  ))}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  200% - 200% - 200%
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className={`h-2 w-2 rounded-full ${
                    i <= 3 ? "bg-gray-800" : "bg-gray-300"
                  }`}
                ></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Health and Wallet & Rewards Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Health Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Customer Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                <ArrowDown className="h-4 w-4 text-gray-600" />
              </div>
              <span className="text-sm text-gray-700">High Risk of Churn</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-700">
                <span className="font-semibold">2.1</span> visits / month
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                <ArrowDown className="h-4 w-4 text-gray-600" />
              </div>
              <span className="text-sm text-gray-700">
                Spend De: <span className="font-semibold">2 Low</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <MessageCircle className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-700">Low Engagement</span>
            </div>
          </CardContent>
        </Card>

        {/* Wallet & Rewards Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg font-semibold">Wallet & Rewards</CardTitle>
            <Button variant="ghost" size="sm">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-3xl font-semibold text-gray-900">{currency} 420</span>
              </div>
              <div className="text-right">
                <span className="text-3xl font-semibold text-gray-900">
                  3,200
                </span>
                <p className="text-sm text-gray-600">Points</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">Expiring in 14 days</p>
            <Progress value={60} className="h-2" />
            <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 flex items-center justify-center gap-2">
              Redeem Points
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Purchase Insights and Customer Ledger */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Purchase Insights Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Purchase Insights
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Donut Chart Placeholder */}
            <div className="flex items-center justify-center gap-6">
              <div className="relative h-32 w-32">
                <svg viewBox="0 0 100 100" className="transform -rotate-90">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#e0e7ff"
                    strokeWidth="20"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#a5b4fc"
                    strokeWidth="20"
                    strokeDasharray={`${30 * 2.513} ${100 * 2.513}`}
                    strokeDashoffset="0"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#818cf8"
                    strokeWidth="20"
                    strokeDasharray={`${20 * 2.513} ${100 * 2.513}`}
                    strokeDashoffset={`-${30 * 2.513}`}
                  />
                </svg>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-indigo-100 rounded"></div>
                  <span className="text-sm text-gray-600">00%</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-indigo-300 rounded"></div>
                  <span className="text-sm text-gray-600">30%</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 bg-indigo-500 rounded"></div>
                  <span className="text-sm text-gray-600">20%</span>
                </div>
              </div>
            </div>
            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 bg-indigo-500 rounded"></div>
                <span className="text-sm text-gray-700">
                  Accesories <span className="font-semibold">2.1/month</span>
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Preferred Types: <span className="font-medium">Slim Fit, Casual</span>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Customer Ledger Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Customer Ledger</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Bar Chart Placeholder */}
            <div className="space-y-2">
              <div className="flex items-end justify-between h-32 gap-2">
                {[1, 2, 3, 4, 5, 6].map((i) => {
                  const colors = [
                    "bg-blue-400",
                    "bg-cyan-400",
                    "bg-emerald-400",
                    "bg-teal-400",
                    "bg-violet-400",
                    "bg-purple-400",
                  ];
                  return (
                    <div
                      key={i}
                      className={`flex-1 ${colors[i - 1]} rounded-t opacity-80 hover:opacity-100 transition-opacity`}
                      style={{ height: `${60 + i * 5}%` }}
                    ></div>
                  );
                })}
              </div>
              <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>CORNICHIGGSHOP</span>
                <span>4.004 DSSCHLINGS</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign History and Communication Center Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Campaign History Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">Campaign History</CardTitle>
            <Button variant="link" className="text-sm">
              View All &lt;
            </Button>
          </CardHeader>
          <CardContent>
            <DataTable
              title=""
              data={campaignHistory}
              cellClassName="py-4 text-gray-700"
              columns={[
                {
                  header: "Date",
                  accessorKey: "date",
                },
                {
                  header: "Offer",
                  accessorKey: "offer",
                },
                {
                  header: "Channel",
                  accessorKey: "channel",
                },
                {
                  header: "Revenue",
                  accessorKey: "revenue",
                },
                {
                  header: "Redeemed",
                  accessorKey: "redeemed",
                  cell: (row: any) => (
                    <Badge
                      variant={row.redeemed === "Yes" ? "default" : "secondary"}
                    >
                      {row.redeemed}
                    </Badge>
                  ),
                },
              ]}
            />
          </CardContent>
        </Card>

        {/* Communication Center Card */}
        <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Communication Center
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-2">
            <Checkbox id="communication-center" />
            <Label htmlFor="communication-center" className="text-sm font-medium">
              Communication Center
            </Label>
          </div>

          <div className="space-y-4 border-t pt-4">
            {/* WhatsApp */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox id="whatsapp" defaultChecked />
                <Label htmlFor="whatsapp" className="text-sm">
                  WhatsApp
                </Label>
              </div>
              <RadioGroup defaultValue="opened" className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="opened" id="whatsapp-opened" />
                  <Label htmlFor="whatsapp-opened" className="text-sm cursor-pointer">
                    Opened
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* App Push */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox id="app-push" />
                <Label htmlFor="app-push" className="text-sm">
                  App Push
                </Label>
              </div>
              <RadioGroup defaultValue="no" className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="app-push-no" />
                  <Label htmlFor="app-push-no" className="text-sm cursor-pointer">
                    No
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>

          <Button className="w-full bg-gray-200 hover:bg-gray-300 text-gray-900">
            Send Message
          </Button>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
